<script>
    import { Link } from "svelte-routing";
    import { currentUser } from "../../store/store.js";
    import { formatOrganizationNumber } from "../../utils/profile.js";

    export let favourites = [];

    // Local state to store fetched BRF data
    let favouritesData = {};
    let loading = {};
    let isBatchLoading = false;

    async function loadFavouriteData(id) {
        if (loading[id] || favouritesData[id]) return;

        // Mark as loading to prevent duplicate requests
        loading = { ...loading, [id]: true };

        try {
            const response = await fetch(
                `https://boanalys-backend-324523837899.europe-north1.run.app/get_core?core_id=${id}`,
                {
                    credentials: "include",
                    headers: {
                        Authorization: `Bearer ${$currentUser?.accessToken}`,
                    },
                },
            );

            if (response.ok) {
                const result = await response.json();

                // Align with fetchBrf logic from utils.js
                // result usually has a 'data' property which contains the core info
                let data = { ...result };
                if (result.data) {
                    if (Array.isArray(result.data)) {
                        // If it's an array, take the first one and merge
                        data = { ...data, ...result.data[0] };
                    } else {
                        // If it's an object, merge it
                        data = { ...data, ...result.data };
                        // If there's a nested data array (yearly history), merge first item for basic info
                        if (
                            Array.isArray(result.data.data) &&
                            result.data.data.length > 0
                        ) {
                            data = { ...data, ...result.data.data[0] };
                        }
                    }
                }

                // Ensure brf_name is prioritized if it exists in any of the merged parts
                // Some endpoints might return it as 'name' or 'brf_name'
                data.displayName =
                    data.brf_name || data.name || `Förening ${id}`;

                favouritesData = { ...favouritesData, [id]: data };
            }
        } catch (error) {
            console.error(`Error fetching favourite data for id ${id}:`, error);
        } finally {
            loading = { ...loading, [id]: false };
        }
    }

    // Function to process favourites in batches of 10
    async function processInBatches(ids) {
        if (isBatchLoading) return;
        isBatchLoading = true;

        try {
            const idsToLoad = ids.filter(
                (id) => !favouritesData[id] && !loading[id],
            );
            const batchSize = 10;

            for (let i = 0; i < idsToLoad.length; i += batchSize) {
                const batch = idsToLoad.slice(i, i + batchSize);
                // Load chunk in parallel
                await Promise.all(batch.map((id) => loadFavouriteData(id)));
            }
        } finally {
            isBatchLoading = false;
        }
    }

    // Reactively load data when favourites array changes
    $: if (favourites && favourites.length > 0 && $currentUser?.accessToken) {
        processInBatches(favourites);
    }
</script>

<div class="content-box favourites-box">
    <h4 class="section-title">
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="title-icon"
            ><path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            ></path></svg
        >
        Mina Favoriter
    </h4>

    {#if favourites && favourites.length > 0}
        <div class="favourites-list">
            {#each favourites as id}
                <Link to={`/brf/${id}`} class="favourite-item">
                    <div class="favourite-content">
                        <div class="favourite-icon">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                ><path
                                    d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                                ></path><polyline points="9 22 9 12 15 12 15 22"
                                ></polyline></svg
                            >
                        </div>
                        <div class="favourite-details" style="min-width: 0;">
                            {#if loading[id]}
                                <span
                                    class="favourite-name loading-skeleton"
                                    style="width: 120px;">Laddar...</span
                                >
                                <span
                                    class="favourite-stats loading-skeleton"
                                    style="width: 160px;">Laddar data...</span
                                >
                            {:else if favouritesData[id]}
                                <span
                                    class="favourite-name"
                                    title={favouritesData[id].displayName}
                                >
                                    {favouritesData[id].displayName}
                                </span>
                                <span class="favourite-stats">
                                    {#if favouritesData[id].org_number}
                                        <span class="favourite-stat-badge"
                                            >{formatOrganizationNumber(
                                                favouritesData[id].org_number,
                                            )}</span
                                        >
                                    {:else}
                                        <span class="favourite-stat-badge"
                                            >Org saknas</span
                                        >
                                    {/if}

                                    {#if favouritesData[id].municipality}
                                        <span class="favourite-stat-badge"
                                            >{favouritesData[id]
                                                .municipality}</span
                                        >
                                    {/if}

                                    {#if favouritesData[id].number_of_apartments}
                                        <span class="favourite-stat-badge"
                                            >{favouritesData[id]
                                                .number_of_apartments} lgh</span
                                        >
                                    {/if}
                                </span>
                            {:else}
                                <span class="favourite-name">Förening {id}</span
                                >
                                <span class="favourite-stats">
                                    <span class="favourite-stat-badge"
                                        >Kunde inte hämta data</span
                                    >
                                </span>
                            {/if}
                        </div>
                        <div class="favourite-arrow">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                ><polyline points="9 18 15 12 9 6"
                                ></polyline></svg
                            >
                        </div>
                    </div>
                </Link>
            {/each}
        </div>
    {:else}
        <div class="favourites-empty">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="empty-icon"
                ><path
                    d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                ></path></svg
            >
            <p>Du har inga sparade favoriter ännu.</p>
            <span
                >Gå till en förening och klicka på stjärnan för att spara den
                här.</span
            >
        </div>
    {/if}
</div>
